import { useState, useEffect, useCallback } from 'react'
import { fetchLeadSettings, saveLeadSettings, resetLeadSettings } from '../api/leadApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export default function useLeadSettings() {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false)

  const loadSettings = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchLeadSettings()
      setSettings(data)
    } catch (e) {
      console.error('Failed to load lead settings', e)
      appToast.error('Failed to load lead form settings.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const updateField = (fieldKey, property, value) => {
    setSettings((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        [fieldKey]: {
          ...prev[fieldKey],
          [property]: value,
        },
      }
    })
  }

  const handleSave = async () => {
    if (!settings) return
    setIsSaving(true)
    try {
      await saveLeadSettings(settings)
      appToast.success('Settings saved successfully')
    } catch (e) {
      console.error('Failed to save lead settings', e)
      appToast.error('Error saving settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    const ok = await appConfirm({
      title: 'Reset Form Settings',
      message: 'Reset all lead form fields to system defaults?',
      confirmText: 'Reset',
      isDestructive: true,
    })
    if (ok) {
      try {
        const defaults = await resetLeadSettings()
        setSettings(defaults)
        appToast.info('Lead form settings restored to defaults.')
      } catch (e) {
        console.error('Failed to reset lead settings', e)
        appToast.error('Failed to reset settings.')
      }
    }
  }

  return {
    state: {
      settings,
      loading,
      isSaving,
      isLivePreviewOpen,
    },
    actions: {
      updateField,
      handleSave,
      handleReset,
      setIsLivePreviewOpen,
      loadSettings,
    },
  }
}
